/**
 * Shared dispatch tab rules (keep in sync with DispatchScreen tab filters).
 */

export function isDoorDeliveryOrder(order) {
  return order?.deliveryFeeType === 'deliver_to_door' || order?.deliveryFeeType === 'delivery_to_door';
}

// Door-delivery orders never get a third-party store cart, so the
// "dispatched" handoff is reached as soon as a store reference is set
// (operator pasted a confirmation URL or a legacy storeOrderId). Status
// can be `requested` (admin-saved through the dispatch endpoint) or
// `paymentCompleted`/`pending`/`requested` once the operator has placed
// it. We deliberately do NOT require `storeSpend > 0` (door deliveries
// often have no separate cart) and do NOT require `driverID` /
// `storeArrivalTime` (driver/arrival flow is for non-door orders only).
//
// This shared helper is what fixes the "deliver_to_door order returns to
// Awaiting Dispatch after refresh" bug — both the dispatched bucket and
// the awaiting-dispatch bucket now agree on what counts as a dispatched
// door order, so the row can no longer leak between the two on reload.
export function isDoorDeliveryDispatched(order) {
  if (!order) return false;
  if (!isDoorDeliveryOrder(order)) return false;
  const orderId = (order.storeOrderId || '').toString().trim();
  const orderUrl = (order.storeOrderConfirmationUrl || '').toString().trim();
  if (!orderId && !orderUrl) return false;
  const status = order.status;
  return (
    status === 'requested' ||
    status === 'paymentCompleted' ||
    status === 'pending' ||
    status === 'picked'
  );
}

function getResolvedEntryInstructions(order) {
  return String(
    order?.entryInstructions ||
    order?.property?.description ||
    ''
  ).trim();
}

export function hasDispatchEntryInstructions(order) {
  if (order?.needsEntryInstructions === true) return false;
  if (order?.hostOrderDetailsInputted === true) return true;
  return Boolean(getResolvedEntryInstructions(order));
}

export function hasDispatchDeliveryTime(order) {
  if (isDoorDeliveryOrder(order)) return true;
  if (order?.needsDeliveryTime === true) return false;
  return Boolean(order?.storeArrivalTime || order?.hostSetDeliveryTime);
}

export function needsDispatchEntryInstructions(order) {
  return !hasDispatchEntryInstructions(order);
}

export function needsDispatchDeliveryTime(order) {
  return !hasDispatchDeliveryTime(order);
}

export function needsDispatchAttention(order) {
  return needsDispatchEntryInstructions(order) || needsDispatchDeliveryTime(order);
}

/**
 * Assign Driver tab: pending or requested, store order placed, driver pay set,
 * and no driver on the resolved parent order (main order or add-on parent).
 *
 * @param {object} order
 * @param {object[]} allOrders - Full list for originalOrderId → parent lookup
 */
export function isAssignDriverOrder(order, allOrders = []) {
  if (isDoorDeliveryOrder(order)) return false;
  if (order.status !== 'pending' && order.status !== 'requested') return false;

  const spend = parseFloat(order.storeSpend);
  if (isNaN(spend) || spend <= 0) return false;

  const pay = parseFloat(order.driverPay);
  if (isNaN(pay) || pay <= 0) return false;

  if (!order.storeOrderId?.trim()) return false;

  const parent = order.originalOrderId
    ? allOrders.find((p) => String(p._id) === String(order.originalOrderId))
    : null;
  const resolved = parent || order;
  return !resolved.driverID;
}
